import { useEffect, useState, useRef } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { HeaderTitleInfo } from "../../../interfaces/HeaderTitleInfo";
import {
  useConfigState,
  useVrsTranslationState,
} from "../../../context/AppContext/AppContext";
import LinkToUrl from "../../../components/Basic/LinkToUrl/LinkToUrl";
import { styled } from "@mui/material/styles";
import { myPrimarySelectedColor } from "../../../constants/colors";
import {
  myVJBlack500,
  myVJBlack900,
  myVJBlue100,
} from "../../../constants/brandingColors";
import { Box, Tooltip } from "@mui/material";

const transitionBase = 'all .2s ease-in-out';

const StyledLinkToUrl = styled(LinkToUrl)(() => ({
  textDecoration: 'none !important',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'none !important',
  },
  '&:visited': {
    textDecoration: 'none !important',
  },
  '&:focus': {
    textDecoration: 'none !important',
  },
}));

const AppHeaderTitleContainer = styled("div")(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  alignItems: 'flex-start',
  transition: transitionBase,
  position: 'relative',
  color: myVJBlue100,
  padding: 0,
  '& .MuiTypography-root': {
    fontSize: '12px', // Smaller font for mobile breadcrumbs
    color: myVJBlue100,
  }
}));

const AppHeaderTitleHeadingContainer = styled("div")({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden', // Add this
  minWidth: 0, // Add this to allow flex items to shrink
  width: '100%', // Add this to constrain the width
});

const ItemParentContainer = styled("div")({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap', // Prevent text wrapping
  minWidth: 0, // Allow this container and its children to shrink
});

const BreadcrumbItemContainer = styled("div")<{ lastLeaf?: boolean }>(({ lastLeaf }) => ({
  fontSize: '12px',
  fontWeight: 400,
  margin: 0,
  color: lastLeaf ? myVJBlack900 : myVJBlue100, // Conditional color based on lastLeaf prop
  whiteSpace: 'nowrap', // Prevent text wrapping
  overflow: 'hidden', // Hide overflow if text is too long
  textOverflow: 'ellipsis', // Show ellipsis (...) for cut-off text
  // minWidth: 0, // Allow shrinking below content size
  // flexShrink: 1, // Allow this item to shrink
}));

const AppHeaderTitleHeadingTextSpan = styled("span")(({ theme }) => ({
  fontSize: theme.spacing(2.5),
  fontWeight: 700,
  margin: 0,
  color: myVJBlue100
}));

const LinkSpan = styled("span")(() => ({
  color: myVJBlue100,
  textDecorationColor: myVJBlue100,
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': {
    color: myPrimarySelectedColor,
  }
}));

const NormalSpan = styled("span")(() => ({
  color: myVJBlack900,
  textDecorationColor: myVJBlack900,
  textDecoration: 'none',
}));

const SeparatorContainer = styled("div")(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: myVJBlue100,
  marginLeft: theme.spacing(0.625),
  marginRight: theme.spacing(0.625),
}));

const MainIconContainer = styled("div")(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: myVJBlue100,
  marginRight: theme.spacing(1)
}));

// New component to conditionally show tooltip based on overflow
const ConditionalTooltip = ({ children, tooltipText, tooltipStyles, lastLeaf }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        // Check if the element's content is wider than its visible width
        const isOverflowing = element.scrollWidth > element.clientWidth;
        setShowTooltip(isOverflowing);
      }
    };

    checkOverflow();

    // Use ResizeObserver to check when the element size changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [tooltipText]); // Re-run when text changes

  return (
    <Tooltip
      title={showTooltip ? tooltipText : ''}
      placement="bottom"
      enterDelay={500}
      leaveDelay={200}
      disableHoverListener={!showTooltip} // Disable tooltip when not overflowing
      slotProps={{
        tooltip: {
          sx: tooltipStyles.tooltip
        }
      }}
    >
      <BreadcrumbItemContainer ref={textRef} lastLeaf={lastLeaf}>
        {children}
      </BreadcrumbItemContainer>
    </Tooltip>
  );
};

interface HeaderTitleProps extends HeaderTitleInfo {
  breadcrumbVisible: boolean;
  titleVisible: boolean;
  textOnly?: boolean;
}

function HeaderTitle({
  titleId,
  icon,
  linkParams,
  breadcrumbVisible,
  textOnly,
  titleVisible,
}: HeaderTitleProps) {
  const { _T } = useVrsTranslationState();
  const [breadCrumbData, setBreadCrumbData] = useState<any>(null);

  const configState = useConfigState();

  const tooltipStyles = {
    tooltip: {
      backgroundColor: 'white',
      color: 'black',
      fontSize: '12px',
      fontFamily: 'Poppins, sans-serif',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: '4px',
      padding: '8px 12px',
      maxWidth: '300px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    }
  };

  const tooltipStylesForLink = {
    tooltip: {
      backgroundColor: 'white',
      color: myVJBlue100,
      fontSize: '12px',
      fontFamily: 'Poppins, sans-serif',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: '4px',
      padding: '8px 12px',
      maxWidth: '300px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    }
  }

  useEffect(() => {
    if (titleId && configState.locale) {
      setBreadCrumbData(linkParams || {});
    }
  }, [titleId, linkParams, configState.locale, textOnly, titleVisible]);

  useEffect(() => {
    document.title = `VideojetConnect™ - ${textOnly ? titleId : _T(titleId)}`;
  }, [titleId]);

  return (
    <>
      <AppHeaderTitleContainer>
        {titleVisible && (
          <AppHeaderTitleHeadingContainer>
            <ItemParentContainer>
              <MainIconContainer> {icon}</MainIconContainer>
              <AppHeaderTitleHeadingTextSpan>
                {textOnly ? titleId : _T(titleId)}
              </AppHeaderTitleHeadingTextSpan>
            </ItemParentContainer>
          </AppHeaderTitleHeadingContainer>
        )}
        {breadcrumbVisible && (
          <AppHeaderTitleHeadingContainer>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              minWidth: 0,
              width: '100%',
            }}>
              {breadCrumbData ? (
                <>
                  {breadCrumbData.parents && (
                    <ItemParentContainer>
                      <ConditionalTooltip
                        tooltipText={_T("Home")}
                        tooltipStyles={tooltipStyles}
                        lastLeaf={true}
                      >
                        <StyledLinkToUrl to={'/home'}>
                          <LinkSpan>
                            {_T("Home")}
                          </LinkSpan>
                        </StyledLinkToUrl>
                      </ConditionalTooltip>
                    </ItemParentContainer>
                  )}
                  {breadCrumbData.parents &&
                    breadCrumbData.parents.map((el, index) => (
                      <ItemParentContainer
                        key={`${el.id}-${index}`}
                      >

                        <SeparatorContainer>
                          <ArrowForwardIosIcon fontSize="small" data-testid="ArrowForwardIosIcon" sx={{ color: myVJBlack500 }} />
                        </SeparatorContainer>

                        <ConditionalTooltip
                          tooltipText={`${el.textOnly ? el.id : el.id && _T(el.id)}`}
                          tooltipStyles={el.link ? tooltipStylesForLink : tooltipStyles}
                          lastLeaf={index === breadCrumbData.parents.length - 1}
                        >
                          {el.link ? (
                            <StyledLinkToUrl to={el.link}>
                              <LinkSpan>
                                {el.textOnly ? el.id : el.id && _T(el.id)}
                              </LinkSpan>
                            </StyledLinkToUrl>
                          ) : (
                            <NormalSpan>
                              {el.textOnly ? el.id : el.id && _T(el.id)}
                            </NormalSpan>
                          )}
                        </ConditionalTooltip>
                      </ItemParentContainer>
                    ))}

                </>
              ) : null}
            </Box>
          </AppHeaderTitleHeadingContainer>
        )}
      </AppHeaderTitleContainer>
    </>
  );
}

export default HeaderTitle;
